import { isDefined } from "@clipboard-health/util-ts";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ApiResponse, get, type GetApiParams } from "./api";
import { getTransportAwareRetry } from "./apiTransportRecovery";
import { type ReactQueryMeta } from "./types";

type UseQueryResponseType<ResponseSchema> = ApiResponse<ResponseSchema>["data"];

type LocallyDefinedUseQueryOptions = "meta" | "queryKey" | "queryFn";
type DisallowedUseQueryOptions = "onSuccess" | "onError" | "onSettled";

export type UseGetQueryOptions<
  ResponseSchema,
  SelectData = UseQueryResponseType<ResponseSchema>,
> = Omit<
  UseQueryOptions<
    UseQueryResponseType<ResponseSchema>,
    AxiosError,
    SelectData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >,
  LocallyDefinedUseQueryOptions | DisallowedUseQueryOptions
>;

export type UseGetQueryProps<
  RequestSchema,
  ResponseSchema,
  SelectSchema = ResponseSchema,
> = GetApiParams<RequestSchema, ResponseSchema> &
  UseGetQueryOptions<ResponseSchema, SelectSchema> & {
    // We require `meta`
    meta: ReactQueryMeta;
  };

/**
 * Builds the query key shape that {@link useGetQuery} uses internally, so consumers that reach
 * for the same underlying fetch (e.g. `useQueries` sharing one URL across many IDs) can dedupe
 * against it without duplicating the literal.
 */
export function getUseGetQueryKey(url: string, queryParams?: unknown): readonly unknown[] {
  return [url, queryParams];
}

export function useGetQuery<RequestSchema, ResponseSchema, SelectSchema = ResponseSchema>(
  props: UseGetQueryProps<RequestSchema, ResponseSchema, SelectSchema>
): UseQueryResult<SelectSchema> {
  const {
    url,
    queryParams,
    requestSchema,
    responseSchema,
    customHeaders,
    disableGetTransportRecovery,
    paramsSerializer,
    retry,
    ...options
  } = props;

  return useQuery({
    ...options,
    ...(isDefined(retry) ? { retry: getTransportAwareRetry(retry) } : {}),
    queryKey: getUseGetQueryKey(url, queryParams),
    queryFn: async () => {
      const response = await get({
        url,
        queryParams,
        requestSchema,
        responseSchema,
        customHeaders,
        disableGetTransportRecovery,
        paramsSerializer,
      });
      return response.data;
    },
  });
}
